/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */

import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';

import { registerPreviewOnlyIdGuard } from '@/lib/crm/store';

import {
  getActivePreview,
  registerActivePreview,
  subscribeToActivePreview,
} from './activePreviewRegistry';
import { createPreviewStore, type PreviewStore } from './createPreviewStore';
import type { CrmPreviewHandle, PreviewOverlay } from './types';

const CrmPreviewContext = createContext<CrmPreviewHandle | null>(null);

export function useCrmPreview(): CrmPreviewHandle | null {
  return useContext(CrmPreviewContext);
}

const NOOP_REGISTRY_SUBSCRIBE = (_cb: () => void) => () => {};
const NULL_REGISTRY_SNAPSHOT = () => null;

/**
 * The preview handle for THIS render position: the context handle when inside
 * a `CrmPreviewProvider` subtree, otherwise the currently-registered ACTIVE
 * provider from `activePreviewRegistry` (re-rendering when one mounts,
 * unmounts, or is superseded).
 *
 * Use this — not `useCrmPreview` — from anything that can render OUTSIDE the
 * provider subtree while a preview is active: the record slideout (portaled at
 * the app root / hosted as a SplitView sibling) and everything rendered
 * inside it (store read hooks, clickable entity wrappers). Deliberate
 * semantics: while a proposal surface is mounted with previews, projected
 * CRM state is visible everywhere it renders — not only inside the provider
 * subtree. With no provider mounted anywhere this returns null, so all
 * consumers stay byte-for-byte at baseline behavior.
 *
 * Consumers INSIDE a provider subtree do not subscribe to the registry at
 * all (noop subscription, constant snapshot): the context wins there, so
 * registry churn — another surface's provider mounting/unmounting — must not
 * re-render every chip and cell under this provider.
 */
export function useActivePreviewHandle(): CrmPreviewHandle | null {
  const contextHandle = useCrmPreview();
  const hasContext = contextHandle !== null;
  const registrySubscribe = useMemo(
    () => (hasContext ? NOOP_REGISTRY_SUBSCRIBE : subscribeToActivePreview),
    [hasContext]
  );
  const registryGetSnapshot = useMemo(
    () => (hasContext ? NULL_REGISTRY_SNAPSHOT : getActivePreview),
    [hasContext]
  );
  const registryHandle = useSyncExternalStore(
    registrySubscribe,
    registryGetSnapshot
  );
  return contextHandle ?? registryHandle;
}

function toMap(
  rec: Record<string, PreviewOverlay>
): Map<string, PreviewOverlay> {
  return new Map(Object.entries(rec));
}

export function CrmPreviewProvider({
  overlays,
  children,
}: {
  overlays: Record<string, PreviewOverlay>;
  children: ReactNode;
}) {
  const storeRef = useRef<PreviewStore | null>(null);
  if (storeRef.current === null) {
    const s = createPreviewStore();
    s.setOverlays(toMap(overlays));
    storeRef.current = s;
  }
  const store = storeRef.current;

  useEffect(() => {
    store.setOverlays(toMap(overlays));
  }, [store, overlays]);

  // Registers the DEV-only `upsertObject` poisoning guard (crmObjectStore.ts)
  // for the lifetime of this provider: a `create` overlay's synthetic id must
  // never reach the real store. Binding contract for the future proposals
  // provider — a `create` overlay MUST be torn down (removed from `overlays`)
  // BEFORE the real entity with that id is upserted into the store, or the
  // upsert is silently refused in DEV.
  useEffect(
    () =>
      registerPreviewOnlyIdGuard(
        id => store.handle.getOverlay(id)?.kind === 'create'
      ),
    [store]
  );

  // Publishes this provider's handle to the module-level registry so
  // consumers rendered OUTSIDE this subtree (the root-portaled slideout and
  // the action detail page's SplitView record panel) can resolve overlays via
  // `useOverlaySubscription`'s fallback. Layout effect so an already-open
  // slideout re-reads its overlay before paint on provider remounts (e.g.
  // proposal switches keyed on action id) instead of flashing not-found.
  useLayoutEffect(() => registerActivePreview(store.handle), [store]);

  return (
    <CrmPreviewContext.Provider value={store.handle}>
      {children}
    </CrmPreviewContext.Provider>
  );
}
